import React, { useCallback, useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import Navbar from '../../components/Navbar';
import axiosAuth from '../../config/axios';
import { IconShield } from '../../components/Icons';
import { diaLocal, estilos as s } from './comun';
import { BarrasH, Columnas, Indicador, numero } from './graficos';

// Indicadores agregados de Bienestar: los ven orientación, director y admin.
// Solo cifras; cualquier desglose con menos de 5 llega del servidor como "< 5".

function periodo(tipo) {
  const hoy = new Date();
  const hasta = diaLocal(hoy);
  if (tipo === '30') { const d = new Date(hoy); d.setDate(d.getDate() - 30); return { desde: diaLocal(d), hasta }; }
  if (tipo === '180') { const d = new Date(hoy); d.setMonth(d.getMonth() - 6); return { desde: diaLocal(d), hasta }; }
  return { desde: `${hoy.getFullYear()}-01-01`, hasta };
}

const PERIODOS = [
  { valor: 'anio', nombre: 'Este año' },
  { valor: '180', nombre: 'Últimos 6 meses' },
  { valor: '30', nombre: 'Últimos 30 días' },
  { valor: 'otro', nombre: 'Otro rango' },
];

const pctTexto = (v) => (v === null || v === undefined ? '—' : `${v} %`);
const promTexto = (p, unidad) => (p?.menor_5 ? '< 5 casos' : p?.valor === null || p?.valor === undefined ? '—' : `${String(p.valor).replace('.', ',')} ${unidad}`);
function horasTexto(p) {
  if (p?.menor_5) return '< 5 casos';
  if (p?.valor === null || p?.valor === undefined) return '—';
  return p.valor >= 48 ? `${String(Math.round(p.valor / 2.4) / 10).replace('.', ',')} días` : `${String(p.valor).replace('.', ',')} h`;
}

function exportar(d) {
  const wb = XLSX.utils.book_new();
  const hoja = (filas, nombre) => XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(filas), nombre);
  hoja([
    ['Indicadores de Bienestar y Orientación'],
    ['Período', `${d.rango.desde} a ${d.rango.hasta}`],
    ['Nota', 'Cifras agregadas. "< 5" indica menos de 5 casos, oculto para proteger la identidad.'],
    [],
    ['Indicador', 'Valor'],
    ['Remisiones recibidas', d.remisiones.total],
    ['Remisiones originadas en señales', d.remisiones.desde_senal],
    ['Tiempo promedio de respuesta', horasTexto(d.remisiones.horas_respuesta)],
    ['Casos activos (hoy)', d.casos.activos],
    ['Casos abiertos en el período', d.casos.abiertos],
    ['Casos cerrados en el período', d.casos.cerrados],
    ['Tiempo promedio de atención', promTexto(d.casos.dias_atencion, 'días')],
    ['Seguimientos registrados', d.seguimientos],
    ['Planes vigentes (hoy)', d.planes.vigentes],
    ['Planes creados', d.planes.creados],
    ['Planes cumplidos', d.planes.cumplidos],
    ['Citas', d.citas.total],
    ['Citas realizadas', d.citas.realizadas],
    ['Citas sin asistencia', d.citas.no_asistio],
    ['Citas canceladas', d.citas.canceladas],
    ['Asistencia a citas', pctTexto(d.citas.asistencia_pct)],
    ['Compromisos', d.compromisos.total],
    ['Compromisos cumplidos', d.compromisos.cumplidos],
    ['Cumplimiento de compromisos', pctTexto(d.compromisos.cumplimiento_pct)],
    ['Señales detectadas', d.senales.detectadas],
    ['Señales remitidas', d.senales.remitidas],
    ['Señales descartadas', d.senales.descartadas],
  ], 'Resumen');
  hoja([['Motivo', 'Remisiones'], ...d.remisiones.por_motivo.map(x => [x.etiqueta, numero(x)])], 'Por motivo');
  hoja([['Grado', 'Remisiones'], ...d.remisiones.por_grado.map(x => [x.etiqueta, numero(x)])], 'Por grado');
  hoja([['Motivo de cierre', 'Casos'], ...d.casos.por_motivo_cierre.map(x => [x.etiqueta, numero(x)])], 'Cierres');
  hoja([['Mes', 'Casos abiertos', 'Casos cerrados'], ...d.casos.por_mes.map(x => [x.mes, x.abiertos, x.cerrados])], 'Por mes');
  XLSX.writeFile(wb, `indicadores-bienestar-${d.rango.desde}-a-${d.rango.hasta}.xlsx`);
}

export default function IndicadoresBienestar() {
  const [tipo, setTipo] = useState('anio');
  const [rango, setRango] = useState(periodo('anio'));
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);
  const [exportando, setExportando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await axiosAuth.get(`/api/bienestar/indicadores?desde=${rango.desde}&hasta=${rango.hasta}`);
      setDatos(r.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar los indicadores');
    } finally {
      setCargando(false);
    }
  }, [rango]);
  useEffect(() => { cargar(); }, [cargar]);

  function elegir(v) {
    setTipo(v);
    if (v !== 'otro') setRango(periodo(v));
  }

  // La exportación se pide al servidor aparte para que quede en la bitácora
  async function descargar() {
    setExportando(true);
    try {
      const r = await axiosAuth.get(`/api/bienestar/indicadores?desde=${rango.desde}&hasta=${rango.hasta}&exportar=1`);
      exportar(r.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo exportar');
    } finally {
      setExportando(false);
    }
  }

  const d = datos;
  return (
    <div style={s.pagina}>
      <Navbar titulo="Indicadores de bienestar" />
      <div style={es.contenido}>
        <div style={es.barra}>
          <div style={es.selector} role="tablist" aria-label="Período">
            {PERIODOS.map(p => (
              <button key={p.valor} role="tab" aria-selected={tipo === p.valor} onClick={() => elegir(p.valor)}
                style={{ ...es.opcion, ...(tipo === p.valor ? es.opcionActiva : {}) }}>{p.nombre}</button>
            ))}
          </div>
          {tipo === 'otro' && (
            <div style={es.fechas}>
              <input type="date" aria-label="Desde" style={{ ...s.input, width: 'auto' }} value={rango.desde} max={rango.hasta}
                onChange={e => e.target.value && setRango({ ...rango, desde: e.target.value })} />
              <span style={{ color: '#999' }}>a</span>
              <input type="date" aria-label="Hasta" style={{ ...s.input, width: 'auto' }} value={rango.hasta} min={rango.desde}
                onChange={e => e.target.value && setRango({ ...rango, hasta: e.target.value })} />
            </div>
          )}
          <button onClick={descargar} disabled={!d || exportando} style={{ ...s.btnSecundario, marginLeft: 'auto' }}>
            {exportando ? 'Exportando...' : 'Exportar a Excel'}
          </button>
        </div>

        <div style={es.aviso}>
          <IconShield size={16} style={{ flexShrink: 0 }} />
          Solo cifras agregadas: no incluyen nombres ni información de ningún caso. Donde hay menos de 5, se muestra “&lt; 5” para proteger la identidad.
        </div>

        {error && <div style={s.errorBox}>{error}</div>}
        {cargando && !d && <p style={s.gris}>Cargando...</p>}

        {d && (
          <div style={{ opacity: cargando ? 0.6 : 1, transition: 'opacity .2s' }}>
            <div style={es.tiles}>
              <Indicador etiqueta="Remisiones" valor={d.remisiones.total.toLocaleString('es-CO')}
                nota={d.remisiones.desde_senal ? `${d.remisiones.desde_senal} desde señales` : 'en el período'} />
              <Indicador etiqueta="Tiempo de respuesta" valor={horasTexto(d.remisiones.horas_respuesta)} nota="de la remisión a su recepción" />
              <Indicador etiqueta="Casos activos" valor={d.casos.activos.toLocaleString('es-CO')} nota="hoy" />
              <Indicador etiqueta="Casos cerrados" valor={d.casos.cerrados.toLocaleString('es-CO')} nota={`${d.casos.abiertos} abiertos en el período`} />
              <Indicador etiqueta="Tiempo de atención" valor={promTexto(d.casos.dias_atencion, 'días')} nota="de la apertura al cierre" />
              <Indicador etiqueta="Asistencia a citas" valor={pctTexto(d.citas.asistencia_pct)} nota={`${d.citas.realizadas} realizadas de ${d.citas.total}`} />
              <Indicador etiqueta="Compromisos cumplidos" valor={pctTexto(d.compromisos.cumplimiento_pct)} nota={d.compromisos.vencidos ? `${d.compromisos.vencidos} vencidos` : 'de los ya cerrados'} />
              <Indicador etiqueta="Planes vigentes" valor={d.planes.vigentes.toLocaleString('es-CO')} nota={`${d.planes.cumplidos} cumplidos en el período`} />
            </div>

            <div style={es.graficos}>
              <Columnas titulo="Casos por mes" subtitulo="Abiertos y cerrados"
                series={[{ clave: 'abiertos', nombre: 'Abiertos' }, { clave: 'cerrados', nombre: 'Cerrados' }]}
                datos={d.casos.por_mes} />
              <BarrasH titulo="Remisiones por motivo" datos={d.remisiones.por_motivo} />
              <BarrasH titulo="Remisiones por grado" datos={d.remisiones.por_grado} />
              <BarrasH titulo="Casos cerrados por motivo" datos={d.casos.por_motivo_cierre} />
            </div>

            <div style={{ ...es.tiles, marginTop: '14px' }}>
              <Indicador etiqueta="Seguimientos registrados" valor={d.seguimientos.toLocaleString('es-CO')} />
              <Indicador etiqueta="Citas canceladas" valor={d.citas.canceladas.toLocaleString('es-CO')} nota={`${d.citas.no_asistio} sin asistencia`} />
              <Indicador etiqueta="Señales detectadas" valor={d.senales.detectadas.toLocaleString('es-CO')}
                nota={`${d.senales.remitidas} remitidas · ${d.senales.descartadas} descartadas`} />
              <Indicador etiqueta="Planes creados" valor={d.planes.creados.toLocaleString('es-CO')} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const es = {
  contenido: { padding: '24px', maxWidth: '1150px', margin: '0 auto', width: '100%' },
  barra: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' },
  selector: { display: 'flex', background: '#fff', borderRadius: '999px', padding: '3px', border: '1px solid #e6e8f0', flexWrap: 'wrap' },
  opcion: { background: 'none', border: 'none', borderRadius: '999px', padding: '6px 14px', fontSize: '13px', fontWeight: 600, color: '#666', cursor: 'pointer', fontFamily: 'inherit' },
  opcionActiva: { background: '#667eea', color: '#fff' },
  fechas: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  aviso: { display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12.5px', color: '#6a6694', background: '#f3f1ff', borderRadius: '10px', padding: '8px 12px', marginBottom: '16px' },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: '12px', marginBottom: '14px' },
  graficos: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))', gap: '14px' },
};
